/**
 * **观众视角的一帧**：把 actor 的手牌读数折成"旁路通道要发的那几条"。
 *
 * 纯函数 —— "第几位、露哪张牌"这种最容易算错一位的东西，交给它在 node 里断言，
 * 而不是塞在 Pixi 的每帧回调里。
 *
 * ⚠️ 观众**只拿到数量**，不拿牌序 —— 牌背长一样，"顺序"没有可观察形式。
 * 所以 hover / reveal / drag 用的一律是**位次（slot）**。
 */
export interface SpectatorFrame {
  readonly count: number;
  readonly hover: number | null;
  /** pointerdown 透露的牌面；没按着就是 null */
  readonly reveal: { readonly slot: number; readonly title: string } | null;
  /** 正在拖的帧（位移单位 = 牌高）；没拖就是 null */
  readonly drag: { readonly slot: number; readonly dx: number; readonly dy: number } | null;
}

export interface SpectatorInput {
  readonly count: number;
  /** 扇形里第 i 位摆的是哪张牌，和 `Hand.order` 同义 */
  readonly order: readonly number[];
  /** hover 的卡牌索引；-1 = 没有 */
  readonly hovered: number;
  /** 按住的卡牌索引；-1 = 没有 */
  readonly held: number;
  readonly drag: { readonly card: number; readonly dx: number; readonly dy: number } | null;
  /** 卡牌索引 → 牌名（只有 reveal 用得到） */
  readonly titleOf: (cardIndex: number) => string | undefined;
}

export function spectatorFrame(input: SpectatorInput): SpectatorFrame {
  const slotOf = (card: number): number | null => {
    const at = input.order.indexOf(card);
    return at < 0 ? null : at;
  };

  const hover = input.hovered < 0 ? null : slotOf(input.hovered);

  let reveal: SpectatorFrame['reveal'] = null;
  if (input.held >= 0) {
    const slot = slotOf(input.held);
    const title = input.titleOf(input.held);
    if (slot !== null && title !== undefined) reveal = { slot, title };
  }

  let drag: SpectatorFrame['drag'] = null;
  if (input.drag !== null) {
    const slot = slotOf(input.drag.card);
    if (slot !== null) drag = { slot, dx: input.drag.dx, dy: input.drag.dy };
  }

  return { count: input.count, hover, reveal, drag };
}

